import { UTILITY_ROOM_UID } from "flavr-common";
import { Matrix4, Vector3 } from "flavr-math";
import { modelPool, modelToAbsolutePath } from "@modules/model";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { camera } from "@modules/rendering/camera";
import { DESKTOP_MARKER_DISTANCE, DESKTOP_MARKER_SCALE, MARKER_MODEL_WIDTH, MAX_SCALE_FOR_MARKERS_ABOVE_ROOMS } from "./MarkerParameters";
import { MarkerMesh } from "./MarkerMesh";
const tmpMarkerTarget = new Vector3();
const tmpAlternateMarkerTarget = new Vector3();
const tmpVector31 = new Vector3();
const tmpVector32 = new Vector3();
const tmpVector33 = new Vector3();
const tmpVector34 = new Vector3();
const tmpMatrix = new Matrix4();
export class MarkerRoomInfo {
    constructor(modelUID, currentRoomUID) {
        this.modelUID = modelUID;
        this.isOpened = false;
        this.markers = new Set();
        this.isCurrentRoom = modelUID === currentRoomUID;
    }
    delete() {
        for (const marker of this.markers.values()) {
            marker.updateRoom();
        }
    }
    onSetClipFadeMessage(msg) {
        this.isOpened = msg.target !== 0;
    }
    onCurrentRoomChanged(currentRoomUID) {
        this.isCurrentRoom = this.modelUID === currentRoomUID;
    }
    updateMarkers(currentRoomUID, callback) {
        if (this.markers.size === 0)
            return;
        const sameRoomMarkers = [];
        const descendantRoomMarkers = [];
        for (const marker of this.markers) {
            if (marker.isInSameRoom(currentRoomUID)) {
                sameRoomMarkers.push(marker);
            }
            else if (marker.isInDescendantRoom(currentRoomUID)) {
                descendantRoomMarkers.push(marker);
            }
            else if (marker.isInAncestorRoom(currentRoomUID)) {
                const target = marker.getAncestorRoomTarget(tmpAlternateMarkerTarget, currentRoomUID);
                if (target === undefined) {
                    marker.visible = false;
                }
                else if (this.isPointInView(target)) {
                    marker.positionAbovePoint(target, MAX_SCALE_FOR_MARKERS_ABOVE_ROOMS);
                }
                else {
                    callback(marker, target);
                }
            }
            else {
                marker.visible = false;
            }
        }
        if (this.isCurrentRoom) {
            const roomModel = modelPool.getModelOrThrow(this.modelUID);
            for (const marker of sameRoomMarkers) {
                const target = marker.getTargetPoint(tmpMarkerTarget, roomModel.getWorldTransform(tmpMatrix));
                if (this.isPointInView(target)) {
                    marker.visible = false;
                }
                else {
                    callback(marker, target);
                }
            }
            return;
        }
        if (descendantRoomMarkers.length === 0)
            return;
        const roomModel = modelPool.getModelOrThrow(this.modelUID);
        const roomCenter = tmpVector31.set(0, 0, 0).applyMatrix4(roomModel.getWorldTransform(tmpMatrix));
        if (this.isPointInView(roomCenter)) {
            this.positionMarkersAboveRoom(descendantRoomMarkers);
        }
        else {
            tmpMarkerTarget.copy(roomCenter);
            for (const marker of descendantRoomMarkers) {
                callback(marker, tmpMarkerTarget);
            }
        }
    }
    isPointInView(point) {
        const proj = tmpVector32.copy(point).project(camera.perspectiveCam);
        return proj.x >= -1 && proj.x <= 1 && proj.y >= -1 && proj.y <= 1 && proj.z <= 1;
    }
    positionMarkersAboveRoom(markers) {
        const numMarkers = markers.length;
        if (numMarkers === 0)
            return;
        const roomModel = modelPool.getModelOrThrow(this.modelUID);
        const roomModelToWorld = roomModel.getWorldTransform(tmpMatrix);
        const topCenter = tmpVector31.set(0, 0.5, 0).applyMatrix4(roomModelToWorld);
        const roomModelWidth = 2 *
            Math.min(tmpVector32.set(0.5, 0.5, 0.0).applyMatrix4(roomModelToWorld).sub(topCenter).length(), tmpVector33.set(0.0, 0.5, 0.5).applyMatrix4(roomModelToWorld).sub(topCenter).length());
        const scale = roomModelWidth * Math.min(1 / (MARKER_MODEL_WIDTH * numMarkers), MAX_SCALE_FOR_MARKERS_ABOVE_ROOMS);
        for (const marker of markers) {
            const pos = tmpMarkerTarget.copy(topCenter);
            marker.positionAbovePoint(pos, scale);
        }
    }
}
export class Marker {
    constructor(rooms, msg) {
        this.rooms = rooms;
        this.targetPoint = new Vector3();
        this.mesh = new MarkerMesh(msg);
    }
    delete() {
        this.mesh.delete();
        if (this.room !== undefined) {
            this.room.markers.delete(this);
        }
    }
    get visible() {
        return this.mesh.visible;
    }
    set visible(visible) {
        this.mesh.visible = visible;
    }
    getTargetPoint(targetPoint, roomTransform) {
        return targetPoint.copy(this.targetPoint).applyMatrix4(roomTransform);
    }
    isInSameRoom(currentRoomUID) {
        return this.targetRoomModelUID === currentRoomUID;
    }
    isInAncestorRoom(currentRoomUID) {
        if (this.targetRoomModelUID === undefined)
            return false;
        const currentRoom = modelPool.getRoomOrThrow(currentRoomUID);
        const targetRoom = modelPool.getRoomOrThrow(this.targetRoomModelUID);
        return targetRoom.isAncestorOf(currentRoom);
    }
    isInDescendantRoom(currentRoomUID) {
        if (this.targetRoomModelUID === undefined)
            return false;
        const currentRoom = modelPool.getRoomOrThrow(currentRoomUID);
        const targetRoom = modelPool.getRoomOrThrow(this.targetRoomModelUID);
        return currentRoom.isAncestorOf(targetRoom);
    }
    getAncestorRoomTarget(targetPoint, currentRoomUID) {
        if (!this.isInAncestorRoom(currentRoomUID) || this.targetRoomModelUID === undefined)
            return undefined;
        const currentRoom = modelPool.getRoomOrThrow(currentRoomUID);
        const targetRoom = modelPool.getRoomOrThrow(this.targetRoomModelUID);
        const actualTarget = this.getTargetPoint(tmpVector31, targetRoom.getWorldTransform(tmpMatrix));
        const exitPointPrefix = `${EXIT_POINT_PRE_NAME}${sanitizeRoomPath(modelToAbsolutePath(currentRoom))}`;
        const utilityRoom = modelPool.getModelOrThrow(UTILITY_ROOM_UID);
        const exitPoints = utilityRoom.getChildren().filter((child) => child.getName().startsWith(exitPointPrefix));
        if (exitPoints.length === 0)
            return undefined;
        let closestExitPoint = exitPoints[0];
        let closestDistance = closestExitPoint.getWorldPosition(tmpVector32).distanceToSquared(actualTarget);
        for (let i = 1; i < exitPoints.length; ++i) {
            const exitPoint = exitPoints[i];
            const distance = exitPoint.getWorldPosition(tmpVector32).distanceToSquared(actualTarget);
            if (distance < closestDistance) {
                closestExitPoint = exitPoint;
                closestDistance = distance;
            }
        }
        return targetPoint.copy(closestExitPoint.getWorldPosition(tmpVector32));
    }
    changeMarkerColor(baseColor, currentRoomUid) {
        if (this.targetRoomModelUID === undefined)
            return;
        this.mesh.changeColor(baseColor, this.targetRoomModelUID !== currentRoomUid);
    }
    updateTarget(targetPosition, targetRoomModelUID, currentRoomUid) {
        this.targetPoint.fromArray(targetPosition);
        if (this.targetRoomModelUID === undefined || this.targetRoomModelUID !== targetRoomModelUID) {
            this.targetRoomModelUID = targetRoomModelUID;
            this.mesh.updateColors(this.targetRoomModelUID !== currentRoomUid);
            this.updateRoom();
        }
    }
    onCurrentRoomChanged(currentRoomUID) {
        if (this.targetRoomModelUID === undefined)
            return;
        this.mesh.updateColors(this.targetRoomModelUID !== currentRoomUID);
    }
    onRoomLoaded(roomModel) {
        if (this.targetRoomModelUID === undefined)
            return;
        const thisTargetRoom = modelPool.getModelOrThrow(this.targetRoomModelUID);
        if (thisTargetRoom.isDescendantOfOrEquals(roomModel)) {
            this.updateRoom();
        }
    }
    updateRoom() {
        if (this.room !== undefined) {
            this.room.markers.delete(this);
            this.room = undefined;
        }
        if (this.targetRoomModelUID === undefined) {
            return;
        }
        const room = this.rooms.get(this.targetRoomModelUID);
        if (room !== undefined) {
            this.room = room;
            room.markers.add(this);
            return;
        }
        const targetRoom = modelPool.getModelOrThrow(this.targetRoomModelUID);
        let parentRoomModel = targetRoom.getRoom();
        while (parentRoomModel !== undefined) {
            const parentRoom = this.rooms.get(parentRoomModel.getUID());
            if (parentRoom !== undefined) {
                this.room = parentRoom;
                parentRoom.markers.add(this);
                return;
            }
            parentRoomModel = parentRoomModel.getRoom();
        }
    }
    positionAtScreenBorder(dx, dy, targetPoint) {
        this.mesh.visible = true;
        const targetPosViewSpace = tmpVector31.copy(targetPoint).applyMatrix4(camera.perspectiveCam.matrixWorldInverse);
        const scale = 1 / Math.max(Math.abs(targetPosViewSpace.x) / dx, Math.abs(targetPosViewSpace.y) / dy);
        targetPosViewSpace.multiplyScalar(scale);
        targetPosViewSpace.z = 0;
        const d = targetPosViewSpace.length();
        tmpVector32.copy(targetPosViewSpace).multiplyScalar(-1).applyQuaternion(localPlayerContainer.cameraWorldOrientation).normalize();
        targetPosViewSpace.z = -1;
        targetPosViewSpace.multiplyScalar(DESKTOP_MARKER_DISTANCE);
        tmpVector31
            .copy(localPlayerContainer.cameraWorldPosition)
            .addScaledVector(localPlayerContainer.cameraWorldViewDirection, DESKTOP_MARKER_DISTANCE)
            .addScaledVector(tmpVector32, (0.5 * DESKTOP_MARKER_SCALE - d) * DESKTOP_MARKER_DISTANCE);
        this.mesh.updateTransform(tmpVector31, tmpVector32, localPlayerContainer.cameraWorldViewDirection, DESKTOP_MARKER_SCALE * localPlayerContainer.getPlatformScale());
    }
    positionInRadar(center, normal, targetPoint, offset, scale) {
        this.mesh.visible = true;
        const platformScale = localPlayerContainer.getPlatformScale();
        const direction = tmpVector31.subVectors(targetPoint, center).normalize();
        const d = normal.dot(direction);
        direction.addScaledVector(normal, -0.9 * d).normalize();
        direction.multiplyScalar(offset * platformScale);
        const markerPos = direction.add(center);
        const markerDir = tmpVector32.subVectors(markerPos, targetPoint).normalize();
        this.mesh.updateTransform(markerPos, markerDir, normal, scale * platformScale);
    }
    positionAbovePoint(point, scale) {
        this.mesh.visible = true;
        const markerDir = tmpVector33.set(0, 1, 0);
        const position = point.addScaledVector(markerDir, 2 * scale); // ToDo: use the actual height of the target room to determine the y-offset
        const markerNormal = tmpVector34.subVectors(position, localPlayerContainer.cameraWorldPosition);
        this.mesh.updateTransform(position, markerDir, markerNormal, scale, true, false);
    }
}
const EXIT_POINT_PRE_NAME = "__exit_to_parent_room_";
function sanitizeRoomPath(path) {
    return path.replace(/[\[\]\/]/g, (c) => c === "[" ? "(" : c === "]" ? ")" : "|");
}
